import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FooterContainer,
  FooterHiddenBox,
  FooterWrapper,
  DisclaimerTip,
  DisclaimerWrapper,
  DisclaimerContainer,
  Wrapper,
  DisclaimerText,
  getPremiumSavingsBannerSlotStyle,
  PREMIUM_SAVINGS_BANNER_SLIDE_MS
} from "./styles";
import CloneChildren from "@components/children";
import { getWidgetSpecificProps } from "@utils/index";
import { connect } from "react-redux";
import DOMPurify from "isomorphic-dompurify";
import ImageContainer from "@components/imageContainer";
import DiscountBanner from "@widgets/discountBanner";
import {
  ConfettiBurst,
  GoodNewsSavingsCounterBanner,
  LoyaltyFinalMorphBanner
} from "@components/common/Lottie";

const mapStateToProps = function (state: any, ownProps: any) {
  return getWidgetSpecificProps(
    state.configuration?.normalisedConfig,
    ownProps.id
  );
};

import { useContext } from "react";
import { Context } from "@components/useContext/context";
import { useOnload } from "@utils/useOnLoad";
import { desktopRightContainerWidth } from "@utils/index";
import { getInrFormat } from "@utils/inrFormatter";
import { buildGoodNewsSlideHeading } from "@utils/premiumSavingsBannerConfig";

type SavingsAnimPhase =
  | "idle"
  | "slide"
  | "good_news"
  | "loyalty_line";

/** ~0.8–1s breathing room between each animation step */
const STEP_GAP_MS = 950;
const INITIAL_BEFORE_SLIDE_MS = 1000;
/** Gap between first and second confetti burst */
const CONFETTI_SECOND_BURST_DELAY_MS = 800;
/** Unmount confetti and show loyalty banner only after this (both bursts finished) */
const CONFETTI_CLEANUP_MS = 2800;

/** M web: scrim above page, below footer (z-index 3) */
const PREMIUM_MWEB_SCRIM_Z = 2;
const PREMIUM_MWEB_SCRIM_BG = "rgba(0, 0, 0, 0.45)";
const Footer = ({
  children,
  disclaimer,
  actions,
  id,
  discount,
  discountBanner,
  wrapperStyles,
  ...props
}: any) => {
  const { isDesktop } = useContext(Context);

  const {
    heading,
    strikeOffHeading,
    subheading,
    coverDiscount,
    strikeOffPrice,
    icon,
    iconAlt,
    showDiscountBanner,
    styles
  } = discountBanner || {};

  const parseAmount = (v: unknown): number => {
    if (v == null || v === "") return NaN;
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    const n = parseFloat(String(v).replace(/,/g, "").replace(/[^\d.-]/g, ""));
    return Number.isNaN(n) ? NaN : n;
  };

  const showBanner = showDiscountBanner === true;

  const loyaltyAmt = parseAmount(coverDiscount);
  const premiumSaveAmt = parseAmount(strikeOffPrice);
  const totalSaveAmt =
    !Number.isNaN(premiumSaveAmt) && !Number.isNaN(loyaltyAmt)
      ? Math.round(premiumSaveAmt + loyaltyAmt)
      : NaN;

  const runPremiumSavingsAnimation =
    showBanner &&
    loyaltyAmt > 0 &&
    !Number.isNaN(premiumSaveAmt) &&
    premiumSaveAmt > loyaltyAmt;

  const [animPhase, setAnimPhase] = useState<SavingsAnimPhase>("idle");
  const [slideRevealed, setSlideRevealed] = useState(false);
  const [confettiBurstIds, setConfettiBurstIds] = useState<number[]>([]);
  /** M web: hide dim after loyalty morph completes */
  const [premiumMwebBackdropReleased, setPremiumMwebBackdropReleased] =
    useState(false);
  const [mwebBackdropPortalReady, setMwebBackdropPortalReady] = useState(false);
  const timersRef = useRef<number[]>([]);
  const slideStartedRef = useRef(false);
  const slideCompleteRef = useRef(false);
  const counterCompleteRef = useRef(false);
  const phaseRef = useRef<SavingsAnimPhase>("idle");
  phaseRef.current = animPhase;

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => {
    setMwebBackdropPortalReady(true);
  }, []);

  useEffect(() => {
    if (!runPremiumSavingsAnimation) {
      clearTimers();
      setAnimPhase("idle");
      setSlideRevealed(false);
      setConfettiBurstIds([]);
      setPremiumMwebBackdropReleased(false);
      slideStartedRef.current = false;
      slideCompleteRef.current = false;
      counterCompleteRef.current = false;
      return undefined;
    }

    clearTimers();
    setAnimPhase("idle");
    setSlideRevealed(false);
    setConfettiBurstIds([]);
    setPremiumMwebBackdropReleased(false);
    slideStartedRef.current = false;
    slideCompleteRef.current = false;
    counterCompleteRef.current = false;

    const tSlide = window.setTimeout(() => {
      setAnimPhase("slide");
      slideStartedRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setSlideRevealed(true));
      });
    }, INITIAL_BEFORE_SLIDE_MS);
    timersRef.current.push(tSlide);

    return () => clearTimers();
  }, [runPremiumSavingsAnimation]);

  const advanceAfterSlide = useCallback(() => {
    if (slideCompleteRef.current || phaseRef.current !== "slide") return;
    slideCompleteRef.current = true;
    const t = window.setTimeout(() => {
      setAnimPhase("good_news");
    }, STEP_GAP_MS);
    timersRef.current.push(t);
  }, []);

  const onSavingsCounterComplete = useCallback(() => {
    if (counterCompleteRef.current || phaseRef.current !== "good_news") return;
    counterCompleteRef.current = true;
    const burst1 = Date.now();
    setConfettiBurstIds([burst1]);
    const tSecond = window.setTimeout(() => {
      setConfettiBurstIds((prev) => [...prev, Date.now()]);
    }, CONFETTI_SECOND_BURST_DELAY_MS);
    const tAfterConfetti = window.setTimeout(() => {
      setConfettiBurstIds([]);
      setAnimPhase("loyalty_line");
    }, CONFETTI_CLEANUP_MS);
    timersRef.current.push(tSecond, tAfterConfetti);
  }, []);

  const onBannerSlideEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.target !== e.currentTarget || e.propertyName !== "transform") {
        return;
      }
      advanceAfterSlide();
    },
    [advanceAfterSlide]
  );

  useEffect(() => {
    if (!runPremiumSavingsAnimation || animPhase !== "slide" || !slideRevealed) {
      return undefined;
    }
    const t = window.setTimeout(
      () => advanceAfterSlide(),
      PREMIUM_SAVINGS_BANNER_SLIDE_MS + 200
    );
    timersRef.current.push(t);
    return () => clearTimeout(t);
  }, [
    runPremiumSavingsAnimation,
    animPhase,
    slideRevealed,
    advanceAfterSlide
  ]);

  const releasePremiumMwebBackdrop = useCallback(() => {
    setPremiumMwebBackdropReleased(true);
  }, []);

  const showPremiumMwebBackdrop =
    mwebBackdropPortalReady &&
    !isDesktop &&
    runPremiumSavingsAnimation &&
    !premiumMwebBackdropReleased;

  const discountBannerStyles = isDesktop
    ? {
        ...styles,
        left: "60%",
        right: "auto",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: desktopRightContainerWidth
      }
    : styles;

  const {
    disclaimerContent,
    disclaimerImage,
    showDisclaimer,
    disclaimerContainerStyles,
    disclaimerType
  } = disclaimer || {};

  const isPreviewPage = id === "page_footer_preview";
  const shouldShowDisclaimer =
    showDisclaimer && (!isPreviewPage || discount !== 0);
  const { on_load } = actions || {};

  useOnload({ on_load });

  const goodNewsHeading = buildGoodNewsSlideHeading(
    getInrFormat(premiumSaveAmt)
  );

  const renderAnimatedBannerCopy = () => {
    if (animPhase === "idle") return null;
    if (animPhase === "slide") {
      return (
        <DiscountBanner
          icon={icon}
          iconAlt={iconAlt}
          heading={goodNewsHeading}
          strikeOffHeading={undefined}
          subheading={undefined}
          strikeOffPrice={0}
          stackedReveal
          styles={styles}
        />
      );
    }
    if (animPhase === "good_news") {
      return (
        <GoodNewsSavingsCounterBanner
          icon={icon}
          iconAlt={iconAlt}
          fromAmount={Math.round(premiumSaveAmt)}
          toAmount={totalSaveAmt}
          styles={styles}
          onCounterComplete={onSavingsCounterComplete}
        />
      );
    }
    if (animPhase === "loyalty_line") {
      return (
        <LoyaltyFinalMorphBanner
          icon={icon}
          iconAlt={iconAlt}
          loyaltyAmt={loyaltyAmt}
          totalSaveAmt={totalSaveAmt}
          styles={styles}
          onAnimationSequenceComplete={releasePremiumMwebBackdrop}
        />
      );
    }
    return null;
  };

  const bannerSlotStyle = getPremiumSavingsBannerSlotStyle({
    isDesktop,
    slideRevealed
  });

  const premiumMwebBackdropNode =
    showPremiumMwebBackdrop &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        role="presentation"
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          background: PREMIUM_MWEB_SCRIM_BG,
          zIndex: PREMIUM_MWEB_SCRIM_Z,
          pointerEvents: "auto",
          touchAction: "none"
        }}
      />,
      document.body
    );

  return (
    <FooterContainer>
      {premiumMwebBackdropNode}
      <FooterHiddenBox />
      <Wrapper>
        {shouldShowDisclaimer && (
          <DisclaimerTip type={disclaimerType}>
            <DisclaimerWrapper>
              <DisclaimerContainer styles={disclaimerContainerStyles}>
                <DisclaimerText
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(disclaimerContent)
                  }}
                />
                {disclaimerImage?.imgSrc ? (
                  <ImageContainer
                    imgSrc={disclaimerImage?.imgSrc}
                    width={disclaimerImage?.width || 16}
                    height={disclaimerImage?.height || 16}
                    alt={disclaimerImage?.alt || "icon"}
                  />
                ) : null}
              </DisclaimerContainer>
            </DisclaimerWrapper>
          </DisclaimerTip>
        )}

        {runPremiumSavingsAnimation ? (
          <div
            style={{
              position: "relative",
              width: "100%",
              alignSelf: "stretch"
            }}
          >
            <div style={{ position: "relative", zIndex: 2 }}>
              <FooterWrapper
                styles={props?.WrapperStyles || {}}
                wrapperStyles={wrapperStyles}
              >
                <CloneChildren
                  styles={{
                    display: "flex",
                    justifyContent: isDesktop ? "space-between" : "unset",
                    flexDirection: !isDesktop && "column-reverse",
                    gap: "8px",
                    width: "100%",
                    ...props.styles
                  }}
                  children={children}
                  props={props}
                />
              </FooterWrapper>
            </div>
            {animPhase !== "idle" && (
              <div
                style={bannerSlotStyle}
                onTransitionEnd={onBannerSlideEnd}
              >
                <div style={{ position: "relative" }}>
                  {confettiBurstIds.map((id) => (
                    <ConfettiBurst key={id} />
                  ))}
                  {renderAnimatedBannerCopy()}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {showBanner && (
              <DiscountBanner
                icon={icon}
                iconAlt={iconAlt}
                heading={heading}
                strikeOffHeading={strikeOffHeading}
                subheading={coverDiscount > 0 ? subheading : undefined}
                strikeOffPrice={strikeOffPrice}
                styles={discountBannerStyles}
              />
            )}
            <FooterWrapper
              styles={props?.WrapperStyles || {}}
              wrapperStyles={wrapperStyles}
            >
              <CloneChildren
                styles={{
                  display: "flex",
                  justifyContent: isDesktop ? "space-between" : "unset",
                  flexDirection: !isDesktop && "column-reverse",
                  gap: "8px",
                  width: "100%",
                  ...props.styles
                }}
                children={children}
                props={props}
              />
            </FooterWrapper>
          </>
        )}
      </Wrapper>
    </FooterContainer>
  );
};

export default connect(mapStateToProps)(Footer);
